import { useEffect, useState } from 'react';

import { useSession } from '@/lib/auth';
import { sessionStatusOf, type SessionStatus } from '@/lib/session-status';

export type { SessionStatus } from '@/lib/session-status';

/**
 * How long the gate waits before it stops waiting.
 *
 * The Better Auth Expo client restores the cached session from
 * expo-secure-store before its `/get-session` request resolves, so a returning
 * person is `signed-in` within a few frames even with the server down. This cap
 * only matters on a first run against an unreachable server, where the request
 * can hang for as long as the OS allows: showing sign-in beats a spinner that
 * never ends.
 */
const BOOTSTRAP_TIMEOUT_MS = 2500;

/**
 * The one question the route gate asks. `data` is the cached session the moment
 * SecureStore gives it up, so `loading` is a few frames, not a screen.
 *
 * `isPending` stays true while the network request is in flight, which is why
 * the presence of `data` is checked first and not last.
 */
export const useSessionStatus = (): SessionStatus => {
  const { data, isPending } = useSession();
  const [waitedLongEnough, setWaitedLongEnough] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setWaitedLongEnough(true), BOOTSTRAP_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  return sessionStatusOf({ hasSession: data !== null, isPending, waitedLongEnough });
};
